
import { useState } from 'react';
import { ArrowLeft, Send, MessageCircle, Mail, Phone } from 'lucide-react';
import Header from '../../components/feature/Header';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Card from '../../components/base/Card';
import Toast from '../../components/base/Toast';

export default function SupportPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    game: '',
    category: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  const categories = [
    'ルールに関する質問',
    'ゲームの進行について',
    'VRChatでの遊び方',
    'システムの不具合',
    'その他'
  ];

  const games = [
    'りゅうたま',
    'カタン',
    'スプレンダー',
    'その他のゲーム'
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setToast({
        message: '必須項目をすべて入力してください',
        type: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/submit/customer_support_form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData
      });

      if (response.ok) {
        setToast({
          message: 'お問い合わせを送信しました。2営業日以内にご返信いたします。',
          type: 'success'
        });
        setFormData({
          name: '',
          email: '',
          game: '',
          category: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('送信に失敗しました');
      }
    } catch (error) {
      setToast({
        message: '送信に失敗しました。しばらく時間をおいて再度お試しください。',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ヘッダー */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => window.REACT_APP_NAVIGATE('/')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            ゲーム一覧に戻る
          </Button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">カスタマーサポート</h1>
          <p className="text-gray-600 leading-relaxed">
            ゲームのルールや遊び方でご不明な点がございましたら、お気軽にお問い合わせください。<br />
            専門スタッフが丁寧にサポートいたします。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* お問い合わせフォーム */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <div className="flex items-center mb-6">
                <MessageCircle className="h-6 w-6 text-blue-600 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">お問い合わせフォーム</h2>
              </div>

              <form id="customer_support_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="name"
                      placeholder="山田太郎"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="email"
                      type="email"
                      placeholder="example@email.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ゲーム名
                    </label>
                    <select
                      name="game"
                      value={formData.game}
                      onChange={(e) => handleInputChange('game', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">選択してください</option>
                      {games.map((game) => (
                        <option key={game} value={game}>{game}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      お問い合わせ種別
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">選択してください</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    件名 <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="subject"
                    placeholder="例：カタンの同点時の勝敗判定について"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    rows={6}
                    placeholder="具体的な状況やご質問内容をお書きください..."
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        handleInputChange('message', e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500文字
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <>送信中...</>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      お問い合わせを送信
                    </>
                  )}
                </Button>
              </form>
            </Card>
          </div>

          {/* サポート情報 */}
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">サポート情報</h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">メール</p>
                    <p className="text-sm text-gray-600">support@gamehelper.com</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Phone className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">電話</p>
                    <p className="text-sm text-gray-600">03-1234-5678</p>
                    <p className="text-xs text-gray-500">平日 10:00-18:00</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">よくある質問</h3>
              
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Q. 回答までどのくらいかかりますか？</p>
                  <p className="text-sm text-gray-600 mt-1">A. 通常5営業日以内にご返信いたします。</p>
                </div>
                
                <div>
                  <p className="font-medium text-gray-900 text-sm">Q. ルールブックはどこで入手できますか？</p>
                  <p className="text-sm text-gray-600 mt-1">A. 各ゲームの公式サイトまたは販売店でご確認ください。</p>
                </div>
                
                <div>
                  <p className="font-medium text-gray-900 text-sm">Q. VRChatでの遊び方がわかりません</p>
                  <p className="text-sm text-gray-600 mt-1">A. 公式サイト等をご確認下さい。</p>
                </div>
              </div>
            </Card>

          </div>
        </div>
      </main>

      {/* トースト通知 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
